/**
 * This is the entry point for Feature Hub App integration
 */

import React from 'react';
import type {
  FeatureAppDefinition,
  FeatureAppEnvironment,
  FeatureServices,
} from '@feature-hub/core';
import type { ReactFeatureApp } from '@feature-hub/react';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import type { Logger } from '@feature-hub/logger';
import { Provider as StoreProvider } from 'react-redux';
import type { GfaLocaleServiceV1 } from '@volkswagen-onehub/gfa-locale-service';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import type { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import type { ContentServiceV1 as ContentService } from '@volkswagen-onehub/audi-content-service';
import type { DealerContextServiceV1 } from '@oneaudi/dealer-context-service';
import type { Reference } from '@apollo/client';
import { ApolloClient, ApolloProvider, InMemoryCache } from '@apollo/client';
import type { FilteredWormwoodVehiclesQuery } from './types';
import { normalizeConfig } from './ConfigMapping/configMapping';
import { store } from './redux';
import App from './FeatureApp';
import { GRAPHQL_GATEWAY_URL, IS_LOCAL_ENV } from './constants/environment';
import { TIER_3_CONFIG } from './constants/mockVehicleForTesting';
import { createMockContentService } from './constants/mockContentService';

const isCypress = typeof window !== 'undefined' ? (window as any).Cypress : '';

export interface FeatureServiceDependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
  readonly 'gfa:service-config-provider': GfaServiceConfigProviderV1;
  readonly 'gfa:locale-service': GfaLocaleServiceV1;
  readonly 'audi-tracking-service': TrackingServiceV2;
  readonly 'audi-content-service'?: ContentService;
  readonly 'dealer-context-service': DealerContextServiceV1;
}

const apolloClient = new ApolloClient({
  uri: GRAPHQL_GATEWAY_URL,
  cache: new InMemoryCache({
    typePolicies: {
      FilteredWormwoodVehicles: {
        fields: {
          vehicles: {
            merge(existing: Reference[], incoming: Reference[], { variables }) {
              const { offset } = variables as FilteredWormwoodVehiclesQuery;
              const merged = existing ? existing.slice(0) : [];

              // eslint-disable-next-line no-plusplus
              for (let i = 0; i < incoming.length; ++i) {
                merged[offset + i] = incoming[i];
              }
              return merged;
            },
          },
        },
      },
      Query: {
        fields: {
          getFilteredVehiclesForWormwood: {
            keyArgs: (_, { variables }) => {
              const { filters, sort, limit, lang, market, preset } =
                variables as FilteredWormwoodVehiclesQuery;

              const cacheKey = [lang, market.join('+'), limit, preset, filters, sort].join('--');
              return cacheKey;
            },
          },
        },
      },
    },
  }),
});

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  dependencies: {
    featureServices: {
      's2:logger': '^1.0.0',
      'gfa:locale-service': '^1.0.0',
      'gfa:service-config-provider': '^1.0.0',
      'audi-tracking-service': '^2.0.0',
      'audi-content-service': '^1.0.0',
      'dealer-context-service': '^1.0.0',
    },
    externals: {
      react: '*',
      'styled-components': '*',
    },
  },

  create: ({
    featureServices,
    config: remoteConfig,
  }: FeatureAppEnvironment<FeatureServiceDependencies, void>) => {
    const loggerService = featureServices['s2:logger'];
    const dealerContextService = featureServices['dealer-context-service'];

    const contentService = (() => {
      if (isCypress && IS_LOCAL_ENV) {
        return (window as any).mockContentService ?? createMockContentService();
      }

      if (IS_LOCAL_ENV) {
        return createMockContentService();
      }

      return featureServices['audi-content-service'];
    })();

    loggerService?.info('Feature App created.');

    const normalConfig = (() => {
      if (isCypress && IS_LOCAL_ENV) {
        return (window as any).mockRemoteConfig ?? TIER_3_CONFIG;
      }

      if (IS_LOCAL_ENV) {
        return TIER_3_CONFIG;
      }

      return normalizeConfig(remoteConfig as any);
    })();

    const configProvider = featureServices['gfa:service-config-provider'];

    return {
      render: () => {
        return (
          <ThemeProvider>
            <StoreProvider store={store}>
              <ApolloProvider client={apolloClient}>
                <App
                  configProvider={configProvider}
                  remoteConfig={normalConfig}
                  dealerContextService={dealerContextService}
                  contentService={contentService}
                />
              </ApolloProvider>
            </StoreProvider>
          </ThemeProvider>
        );
      },
    };
  },
};

export default featureAppDefinition;
